import React, { useEffect, useState } from "react";
import { Loader2, Save, Instagram, Twitter, Youtube, Globe, Music2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * ArtistSocialsCard — verified artists declare their own social handles
 * here. The form posts to ``POST /api/upcoming/artist-socials`` which
 * stores them on the user doc (so AI scans don't overwrite them) AND
 * upserts them straight into ``upcoming_releases.socials`` so the OUT
 * NOW + Coming Soon cards on Home show the links immediately — no
 * waiting for the 6h background scan tick or MusicBrainz verification.
 *
 * Hidden entirely for non-artists (the GET endpoint returns
 * is_artist=false → we return null).
 *
 * Each input accepts either a full URL OR a bare handle (with or
 * without ``@``). Backend canonicalises on save.
 */

const PLATFORMS = [
  { key: "instagram",  label: "Instagram",  icon: Instagram, placeholder: "@yourhandle or full URL" },
  { key: "twitter",    label: "Twitter / X", icon: Twitter,   placeholder: "@yourhandle or full URL" },
  { key: "tiktok",     label: "TikTok",     icon: Music2,    placeholder: "@yourhandle or full URL" },
  { key: "soundcloud", label: "SoundCloud", icon: Music2,    placeholder: "handle or full URL" },
  { key: "youtube",    label: "YouTube",    icon: Youtube,   placeholder: "@channel or full URL" },
  { key: "spotify",    label: "Spotify Artist ID", icon: Music2, placeholder: "artist id or full URL" },
  { key: "homepage",   label: "Website",    icon: Globe,     placeholder: "yoursite.com" },
];

const ArtistSocialsCard = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isArtist, setIsArtist] = useState(false);
  const [values, setValues] = useState({});

  // Initial fetch — also gates visibility (non-artists never see this card)
  useEffect(() => {
    let mounted = true;
    api.get("/upcoming/artist-socials/my")
      .then((r) => {
        if (!mounted) return;
        setIsArtist(!!r.data?.is_artist);
        setValues(r.data?.socials || {});
      })
      .catch(() => { if (mounted) setIsArtist(false); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  const onChange = (key, val) => setValues((v) => ({ ...v, [key]: val }));

  const onSave = async () => {
    setSaving(true);
    try {
      const r = await api.post("/upcoming/artist-socials", values);
      toast.success("Socials saved · live on your Out Now cards now.");
      setValues(r.data?.socials || values);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed. Try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;
  if (!isArtist) return null;            // hidden for listeners

  return (
    <div
      data-testid="artist-socials-card"
      className="p-5 rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.06] to-white/[0.01] mb-6"
    >
      <div className="flex items-start justify-between mb-1">
        <div>
          <p className="text-[10px] uppercase tracking-[0.28em] font-black text-vermilion mb-1">
            Your social handles
          </p>
          <h3 className="font-display text-xl font-semibold leading-tight">
            Verified socials
          </h3>
        </div>
      </div>
      <p className="text-xs text-white/55 mb-5 max-w-lg">
        These links appear on every Out Now + Coming Soon card so listeners
        can follow you straight from the feed. You can type a bare handle
        (<code className="text-vermilion">@yourname</code>) or paste the full URL — we'll clean it up.
        Changes go live immediately, no waiting.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {PLATFORMS.map(({ key, label, icon: Icon, placeholder }) => (
          <label key={key} className="block">
            <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-bold text-white/55 mb-1">
              <Icon size={11} /> {label}
            </span>
            <input
              data-testid={`artist-social-input-${key}`}
              type="text"
              placeholder={placeholder}
              value={values[key] || ""}
              onChange={(e) => onChange(key, e.target.value)}
              className="w-full bg-black/30 border border-white/10 hover:border-white/20 focus:border-vermilion focus:bg-black/50 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30 outline-none transition"
            />
          </label>
        ))}
      </div>

      <div className="mt-5 flex items-center gap-3">
        <button
          data-testid="artist-socials-save-btn"
          onClick={onSave}
          disabled={saving}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-vermilion text-white text-sm font-semibold hover:bg-vermilion-hover transition disabled:opacity-60"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {saving ? "Saving…" : "Save socials"}
        </button>
        <span className="text-[11px] text-white/40">
          {Object.values(values).filter(Boolean).length} platform(s) on file
        </span>
      </div>
    </div>
  );
};

export default ArtistSocialsCard;
